import { cn } from '../../../../../lib/utils';
import type { ConsolidatedDeepCheckResult } from '../../../../../services/admin/adminService';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import type { TFn } from '../types';

/**
 * The long-running per-row actions. Every one of these fetches the email from Gmail and/or calls AI, so
 * none of them may block the ⋯ menu: they are started from the menu but RUN ON THE ROW (see
 * `runRowAction`), which lets the super-admin fire the same check on many rows and read the outcomes
 * side by side afterwards.
 */
export type RowRunKind = 'backprocess' | 'deepAuto' | 'deepTotals' | 'deepPakbon' | 'deepAllFiles' | 'mixed' | 'reaudit' | 'addFactuur' | 'attachFiles' | 'monkeyWrongProcessing';
/** A row action in flight or finished, shown as a chip on its audit row. One per kind per row. */
export type RowRun = {
  status: 'queued' | 'running' | 'ok' | 'error';
  label: string;
  msg?: string;
  /** Mixed check only: the full per-file result, so clicking the chip opens the detail modal without re-running. */
  detail?: ConsolidatedDeepCheckResult;
};
export type RowRunsByKind = Partial<Record<RowRunKind, RowRun>>;

// Client-side gate on how many row actions hit the server at once. Each one is a Gmail fetch + PDF text
// extraction + one or more AI calls, so an impatient click-through of 40 rows would otherwise fire 40
// concurrent runs. Extra clicks queue (and show as such) instead of being dropped.
const ROW_RUN_CONCURRENCY = 10;
let rowRunActive = 0;
const rowRunQueue: Array<() => void> = [];

/** Wait for a free slot; resolves with the release function (call it exactly once, in a finally). */
export function acquireRowRunSlot(): Promise<() => void> {
  const release = () => {
    rowRunActive--;
    const next = rowRunQueue.shift();
    if (next) { rowRunActive++; next(); }
  };
  return new Promise((resolve) => {
    const start = () => resolve(release);
    if (rowRunActive < ROW_RUN_CONCURRENCY) { rowRunActive++; start(); }
    else rowRunQueue.push(start);
  });
}

/**
 * The run's live state on the audit row: a spinner while it queues/runs, then a green/red one-liner
 * (full text in the tooltip). Click a finished chip to clear it — or, when it carries a detail result,
 * to open it (the ✕ clears that one).
 */
export function RowRunChip({ run, onDismiss, onOpen, t }: { run: RowRun; onDismiss: () => void; onOpen?: () => void; t: TFn }) {
  const running = run.status === 'running' || run.status === 'queued';
  const text = run.msg || t(run.status === 'queued' ? 'adminTesting.rowRun.queued' : 'adminTesting.rowRun.running');
  const full = `${run.label} · ${text}`;
  const canOpen = !running && !!onOpen;
  const hint = canOpen ? t('adminTesting.rowRun.openHint') : t('adminTesting.backprocess.dismissHint');
  return (
    <div
      title={running ? full : `${full} · ${hint}`}
      onClick={running ? undefined : (canOpen ? onOpen : onDismiss)}
      className={cn('mt-1 flex items-center gap-1 max-w-[280px] px-1.5 py-0.5 rounded-md text-[10px]',
        running
          ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300'
          : run.status === 'ok'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 cursor-pointer'
            : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 cursor-pointer')}
    >
      {running
        ? <Loader2 className="w-3 h-3 shrink-0 animate-spin" />
        : run.status === 'ok'
          ? <CheckCircle2 className="w-3 h-3 shrink-0" />
          : <AlertTriangle className="w-3 h-3 shrink-0" />}
      <span className="shrink-0 font-semibold">{run.label}</span>
      <span className="truncate">{text}</span>
      {canOpen && (
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          title={t('adminTesting.backprocess.dismissHint')}
          className="shrink-0 ml-0.5 opacity-60 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}
